"use client"

import { useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

interface PressLightboxProps {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function PressLightbox({ src, alt, isOpen, onClose }: PressLightboxProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Use escape key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isOpen]);

  if (!mounted) return null;

  const lightboxContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 md:p-12"
          onClick={onClose}
        >
          {/* Close Button Top Right - High Priority */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 md:top-10 md:right-10 p-4 rounded-full bg-white/10 hover:bg-white/30 transition-all text-white z-[100000] border border-white/20 shadow-2xl"
            aria-label="Close image"
          >
            <X className="w-10 h-10 stroke-[3]" />
          </button>

          {/* Original Size Image container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative max-w-full max-h-full">
               <img
                src={src}
                alt={alt}
                className="max-w-[95vw] max-h-[90vh] object-contain shadow-[0_0_100px_rgba(0,0,0,0.8)] rounded-sm selection:bg-none border border-white/10"
                draggable={false}
              />
              <div className="mt-6 text-center">
                 <p className="text-white/70 text-sm font-serif italic tracking-widest uppercase">
                    {alt || "Digital Edition Intelligence"}
                 </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(lightboxContent, document.body);
}
